import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";

const BUCKET = "contracts";

export type ContractStatus = "utkast" | "sendt" | "signert" | "annullert";
export type ContractTemplateType = "statist" | "ekstern_skuespiller" | "location";

export interface ExtraContract {
  id: string;
  extra_id: string;
  project_id: string | null;
  template_id: string | null;
  template_version: number | null;
  status: ContractStatus;
  pdf_path: string | null;
  signed_pdf_path: string | null;
  pdf_sha256: string | null;
  sent_at: string | null;
  signed_at: string | null;
  signed_name: string | null;
  signed_ip: string | null;
  signed_user_agent: string | null;
  signature_method: string;
  created_by: string | null;
  created_at: string;
  deleted_at: string | null;
}

/**
 * VIB-1281: kontrakter for ett prosjekt. StatisterTab bruker denne til å
 * mappe kontraktstatus per extra_id (nyeste kontrakt vinner ved regenerering).
 */
export function useExtraContracts(projectId: string | undefined) {
  return useQuery({
    queryKey: ["extra-contracts", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("extra_contracts")
        .select("*")
        .eq("project_id", projectId!)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as ExtraContract[];
    },
  });
}

/** Nyeste ikke-slettede kontrakt per extra_id (extra_contracts-lista er allerede sortert nyest først). */
export function contractsByExtraId(contracts: ExtraContract[] | undefined): Map<string, ExtraContract> {
  const map = new Map<string, ExtraContract>();
  for (const c of contracts ?? []) {
    if (!map.has(c.extra_id)) map.set(c.extra_id, c);
  }
  return map;
}

interface GenerateContractResponse {
  contract_id: string;
  pdf_path: string;
  status: ContractStatus;
}

export function useGenerateContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      extra_id: string;
      template_type: ContractTemplateType;
      project_id: string;
    }) => {
      const { data, error } = await supabase.functions.invoke<GenerateContractResponse>(
        "generate-extra-contract",
        { body: { extra_id: input.extra_id, template_type: input.template_type } },
      );
      if (error) throw error;
      if (!data) throw new Error("Tomt svar fra generate-extra-contract");
      return data;
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "extra_contracts", { projectId: vars.project_id });
      toast.success("Kontrakt generert");
    },
    onError: (err: Error) => {
      toast.error(err?.message ?? "Kunne ikke generere kontrakt");
    },
  });
}

interface SendContractResponse {
  success: boolean;
  message: string;
  expires_at: string;
}

/**
 * VIB-1282: sender en generert kontrakt (status='utkast') til signering via
 * det uendrede send-extra-form-mønsteret, men med contract_id satt. Krever
 * innlogget bruker (Supabase-klienten legger på Authorization-headeren
 * automatisk — edge functionen verifiserer JWT for denne grenen).
 */
export function useSendContractToSigning() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { extra_id: string; contract_id: string; project_id: string }) => {
      const { data, error } = await supabase.functions.invoke<SendContractResponse>(
        "send-extra-form",
        { body: { extra_id: input.extra_id, contract_id: input.contract_id } },
      );
      if (error) throw error;
      if (!data) throw new Error("Tomt svar fra send-extra-form");
      return data;
    },
    onSuccess: async (data, vars) => {
      await invalidateFor(qc, "extra_contracts", { projectId: vars.project_id });
      toast.success(data.message ?? "Kontrakt sendt til signering");
    },
    onError: (err: Error) => {
      toast.error(err?.message ?? "Kunne ikke sende kontrakt til signering");
    },
  });
}

/**
 * Signert URL (1t TTL) til en kontrakt-PDF i den private "contracts"-bucketen.
 * Mønster fra getTaskAttachmentUrl (use-task-comments.ts) — aldri getPublicUrl,
 * jf. personvern-kravet i VIB-1281 (personnummer i PDF-innholdet).
 */
export async function getContractPdfUrl(path: string): Promise<string | null> {
  if (!path) return null;
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, 60 * 60);
  if (error) return null;
  return data?.signedUrl ?? null;
}

export function useContractPdfUrl(path: string | null | undefined) {
  return useQuery({
    queryKey: ["extra-contract-pdf-url", path],
    enabled: !!path,
    staleTime: 30 * 60 * 1000,
    queryFn: () => getContractPdfUrl(path!),
  });
}
